import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Button, Input, Panel, Divider } from "@realtyflows/ui";
import { login, googleAuth, saveToken } from "../api";
import Alert from "../components/Alert";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";
import PasswordInput from "../components/PasswordInput";
import useResponsive from "../hooks/useResponsive";
import useScrollToAlert from "../hooks/useScrollToError";

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const errorRef = useScrollToAlert(error);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    document.title = "Login — RealtyFlows";
    return () => { document.title = "RealtyFlows"; };
  }, []);

  const handleGoogleSignIn = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const { token, user } = await googleAuth(idToken, "login");
      saveToken(token);
      onLogin(user);
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") return;
      setError(err.message || "Google sign-in failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      const { token, user } = await login({ email, password });
      saveToken(token);
      onLogin(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: t.pageBg, padding: isMobile ? "24px 16px" : "40px 0" }}>
      <div className="rf-a1" style={{ width: isMobile ? "100%" : 420, maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 20 : 32 }}>
          <div onClick={() => navigate("/")} style={{ display: "inline-flex", alignItems: "center", gap: isMobile ? 8 : 10, marginBottom: 8, cursor: "pointer" }}>
            <img src="/logo.png" alt="RealtyFlows" style={{ width: isMobile ? 24 : 28, height: isMobile ? 24 : 28, borderRadius: 4 }} />
            <span style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Realty<span style={{ color: t.gold }}>Flows</span>
            </span>
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, color: t.textSecondary, fontFamily: font.body }}>Welcome back! Sign in to your account.</div>
        </div>

        {/* Form */}
        <Panel padding={isMobile ? 20 : 32}>
          <Alert ref={errorRef} message={error} onDismiss={() => setError("")} style={{ marginBottom: 16 }} />

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="alex@realtyflows.io" icon="✉" />
            </div>
            <div style={{ marginBottom: 8 }}>
              <PasswordInput label="Password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password" />
            </div>
            <div style={{ textAlign: "right", marginBottom: isMobile ? 16 : 20 }}>
              <span onClick={() => navigate("/forgot-password")} style={{ fontSize: 12, color: t.emerald, cursor: "pointer", fontFamily: font.body }}>Forgot password?</span>
            </div>
            <Button variant="primary" size="lg" fullWidth type="submit" disabled={loading}>
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <Divider label="or" />

          <button onClick={handleGoogleSignIn} disabled={googleLoading}
            style={{
              width: "100%", padding: isMobile ? "10px 14px" : "12px 16px", borderRadius: 10, cursor: googleLoading ? "not-allowed" : "pointer",
              background: t.surfaceWarm, border: `1.5px solid ${t.border}`, display: "flex", alignItems: "center",
              justifyContent: "center", gap: 10, fontFamily: font.body, fontSize: isMobile ? 13 : 14, fontWeight: 600,
              color: t.textPrimary, transition: "all 0.15s", opacity: googleLoading ? 0.7 : 1,
            }}
            onMouseEnter={e => { if (!googleLoading) e.currentTarget.style.borderColor = t.emerald; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {googleLoading ? "Signing in..." : "Continue with Google"}
          </button>

          <div style={{ textAlign: "center", fontSize: isMobile ? 12 : 13, color: t.textSecondary, fontFamily: font.body, marginTop: 16 }}>
            Don't have an account?{" "}
            <span onClick={() => navigate("/signup")} style={{ color: t.emerald, fontWeight: 600, cursor: "pointer" }}>Sign up free</span>
          </div>
        </Panel>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <span onClick={() => navigate("/")} style={{ fontSize: 12, color: t.textMuted, cursor: "pointer", fontFamily: font.body }}>&larr; Back to home</span>
        </div>
      </div>
    </div>
  );
}
